import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlus, faTrash, faCode } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import PropTypes from 'prop-types';

const CreateBlogPost = ({ darkMode, onPostCreated, editPost = null }) => {
    const [formData, setFormData] = useState({
        title: editPost?.title || '',
        category: editPost?.category || '',
        readingTime: editPost?.readingTime || '',
        image: null,
        descriptions: editPost?.descriptions || [{ text: '' }],
        contentSections: editPost?.contentSections || [{ type: 'text', content: '' }]
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({
            ...prev,
            image: e.target.files[0]
        }));
    };

    const addDescription = () => {
        setFormData(prev => ({
            ...prev,
            descriptions: [...prev.descriptions, { text: '' }]
        }));
    };

    const removeDescription = (index) => {
        if (formData.descriptions.length > 1) {
            setFormData(prev => ({
                ...prev,
                descriptions: prev.descriptions.filter((_, i) => i !== index)
            }));
        }
    };

    const updateDescription = (index, text) => {
        setFormData(prev => ({
            ...prev,
            descriptions: prev.descriptions.map((desc, i) => 
                i === index ? { text } : desc
            )
        }));
    };

    const addContentSection = (type = 'text') => {
        setFormData(prev => ({
            ...prev,
            contentSections: [...prev.contentSections, { type, content: '' }]
        }));
    };

    const removeContentSection = (index) => {
        if (formData.contentSections.length > 1) {
            setFormData(prev => ({
                ...prev,
                contentSections: prev.contentSections.filter((_, i) => i !== index)
            }));
        }
    };

    const updateContentSection = (index, content) => {
        setFormData(prev => ({
            ...prev,
            contentSections: prev.contentSections.map((section, i) => 
                i === index ? { ...section, content } : section
            )
        }));
    };

    const updateContentSectionType = (index, type) => {
        setFormData(prev => ({
            ...prev,
            contentSections: prev.contentSections.map((section, i) => 
                i === index ? { ...section, type } : section
            )
        }));
    };

    const resetForm = () => {
        setFormData({
            title: '',
            category: '',
            readingTime: '',
            image: null,
            descriptions: [{ text: '' }],
            contentSections: [{ type: 'text', content: '' }]
        });
        setActiveTab('basic');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Client-side validation
        if (!formData.title.trim()) {
            toast.error('Title is required');
            setIsSubmitting(false);
            return;
        }

        if (!formData.category.trim()) {
            toast.error('Category is required');
            setIsSubmitting(false);
            return;
        }

        if (!formData.readingTime.trim()) {
            toast.error('Reading time is required');
            setIsSubmitting(false);
            return;
        }

        // Validate descriptions
        const validDescriptions = formData.descriptions.filter(desc => desc.text.trim());
        if (validDescriptions.length === 0) {
            toast.error('At least one description is required');
            setIsSubmitting(false);
            return;
        }

        // Validate content sections
        const validContentSections = formData.contentSections.filter(section => section.content.trim());
        if (validContentSections.length === 0) {
            toast.error('At least one content section is required');
            setIsSubmitting(false);
            return;
        }

        try {
            // Debug logging
            console.log('Form data being submitted:', {
                title: formData.title.trim(),
                descriptions: validDescriptions,
                contentSections: validContentSections,
                category: formData.category.trim(),
                readingTime: formData.readingTime.trim(),
                hasImage: !!formData.image
            });

            const submitData = new FormData();
            submitData.append('title', formData.title.trim());
            submitData.append('descriptions', JSON.stringify(validDescriptions));
            submitData.append('contentSections', JSON.stringify(validContentSections));
            submitData.append('category', formData.category.trim());
            submitData.append('readingTime', formData.readingTime.trim());
            if (formData.image) {
                submitData.append('image', formData.image);
            }

            const url = editPost ? `/blog/${editPost._id}` : '/blog/create';
            const method = editPost ? 'put' : 'post';
            
            const response = await api[method](url, submitData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const successMessage = `Blog post ${editPost ? 'updated' : 'created'} successfully!`;
                toast.success(successMessage);
                
                // Only reset form if not in edit mode
                if (!editPost) {
                    resetForm();
                }
                
                // Notify parent component about the successful operation
                onPostCreated && onPostCreated(response.data.post);
            }
        } catch (error) {
            console.error(`Error ${editPost ? 'updating' : 'creating'} blog post:`, error);
            toast.error(error.response?.data?.message || `Failed to ${editPost ? 'update' : 'create'} blog post`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {editPost ? 'Edit Blog Post' : 'Create New Blog Post'}
                </h2>
            </div>

            {/* Form Container */}
            <div className={`rounded-2xl border p-6 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } shadow-lg`}>
                <div className="space-y-6">
                    {/* Tab Navigation */}
                    <div className="flex space-x-1 mb-6 border-b-2 p-1 rounded border-blue-400">
                        <button
                            type="button"
                            onClick={() => setActiveTab('basic')}
                            className={`px-4 py-2 font-medium text-sm rounded transition-colors ${
                                activeTab === 'basic'
                                    ? darkMode
                                        ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                                        : 'bg-blue-500 text-white border-b-2 border-blue-500'
                                    : darkMode
                                        ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                        >
                            Basic Info
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('descriptions')}
                            className={`px-4 py-2 font-medium text-sm  rounded transition-colors ${
                                activeTab === 'descriptions'
                                    ? darkMode
                                        ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                                        : 'bg-blue-500 text-white border-b-2 border-blue-500'
                                    : darkMode
                                        ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                        >
                            Descriptions
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('content')}
                            className={`px-4 py-2 font-medium text-sm  rounded transition-colors ${
                                activeTab === 'content'
                                    ? darkMode
                                        ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                                        : 'bg-blue-500 text-white border-b-2 border-blue-500'
                                    : darkMode
                                        ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                        >
                            Content Sections
                        </button>
                    </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info Tab */}
                    {activeTab === 'basic' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className={`w-full p-3 rounded-lg border ${
                                            darkMode 
                                                ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                                : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                        placeholder="Enter blog post title"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Category *
                                    </label>
                                    <input
                                        type="text"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        className={`w-full p-3 rounded-lg border ${
                                            darkMode 
                                                ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                                : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                        placeholder="e.g., Web Development, Database"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Reading Time *
                                    </label>
                                    <input
                                        type="text"
                                        name="readingTime"
                                        value={formData.readingTime}
                                        onChange={handleInputChange}
                                        className={`w-full p-3 rounded-lg border ${
                                            darkMode 
                                                ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                                : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                        placeholder="e.g., 5 min"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Featured Image
                                    </label>
                                    <input
                                        type="file"
                                        name="image"
                                        onChange={handleFileChange}
                                        accept="image/*"
                                        className={`w-full p-3 rounded-lg border ${
                                            darkMode 
                                                ? 'bg-gray-700 border-gray-600 text-white' 
                                                : 'bg-white border-gray-300 text-gray-800'
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Descriptions Tab */}
                    {activeTab === 'descriptions' && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-medium">Blog Post Descriptions</h3>
                                <button
                                    type="button"
                                    onClick={addDescription}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors flex items-center gap-2`}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    Add Description
                                </button>
                            </div>
                            {formData.descriptions.map((desc, index) => (
                                <div key={index} className="relative">
                                    <div className="flex items-start gap-2">
                                        <div className="flex-1">
                                            <label className="block text-sm font-medium mb-2">
                                                Description {index + 1} *
                                            </label>
                                            <textarea
                                                value={desc.text}
                                                onChange={(e) => updateDescription(index, e.target.value)}
                                                rows="3"
                                                className={`w-full p-3 rounded-lg border ${
                                                    darkMode 
                                                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                                        : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                                } focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none`}
                                                placeholder={`Enter description ${index + 1}...`}
                                                required
                                            />
                                        </div>
                                        {formData.descriptions.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeDescription(index)}
                                                className="mt-8 p-2 text-red-500 hover:text-red-700 transition-colors"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Content Sections Tab */}
                    {activeTab === 'content' && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-medium">Content Sections</h3>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => addContentSection('text')}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white transition-colors flex items-center gap-2`}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        Add Text
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => addContentSection('code')}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white transition-colors flex items-center gap-2`}
                                    >
                                        <FontAwesomeIcon icon={faCode} />
                                        Add Code
                                    </button>
                                </div>
                            </div>
                            {formData.contentSections.map((section, index) => (
                                <div key={index} className="relative border rounded-lg p-4 ${darkMode ? 'border-gray-600' : 'border-gray-300'}">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <label className="block text-sm font-medium">
                                                Section {index + 1} Type:
                                            </label>
                                            <select
                                                value={section.type}
                                                onChange={(e) => updateContentSectionType(index, e.target.value)}
                                                className={`px-3 py-1 rounded border text-sm ${
                                                    darkMode 
                                                        ? 'bg-gray-700 border-gray-600 text-white' 
                                                        : 'bg-white border-gray-300 text-gray-800'
                                                }`}
                                            >
                                                <option value="text">Text/Markdown</option>
                                                <option value="code">Code Block</option>
                                            </select>
                                        </div>
                                        {formData.contentSections.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeContentSection(index)}
                                                className="p-2 text-red-500 hover:text-red-700 transition-colors"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        )}
                                    </div>
                                    <textarea
                                        value={section.content}
                                        onChange={(e) => updateContentSection(index, e.target.value)}
                                        rows={section.type === 'code' ? '8' : '6'}
                                        className={`w-full p-3 rounded-lg border ${
                                            darkMode 
                                                ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                                : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none ${
                                            section.type === 'code' ? 'font-mono text-sm' : ''
                                        }`}
                                        placeholder={section.type === 'code' 
                                            ? 'Enter your code here...\n\n// Example:\nfunction hello() {\n  console.log("Hello World!");\n}' 
                                            : 'Enter your content here... You can use Markdown formatting.'
                                        }
                                        required
                                    />
                                    {section.type === 'text' && (
                                        <p className="text-sm text-gray-500 mt-2">
                                            Tip: You can use Markdown formatting (## for headings, **bold**, *italic*, etc.)
                                        </p>
                                    )}
                                    {section.type === 'code' && (
                                        <p className="text-sm text-gray-500 mt-2">
                                            This will be displayed as a formatted code block with syntax highlighting.
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex justify-end gap-4 pt-4 border-t border-gray-600/30">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`px-6 py-3 rounded-lg font-medium text-white ${
                                darkMode 
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500' 
                                    : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                            } disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2`}
                        >
                            {isSubmitting && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                            {isSubmitting ? (editPost ? 'Updating...' : 'Creating...') : (editPost ? 'Update Post' : 'Create Post')}
                        </button>
                    </div>
                </form>
                </div>
            </div>
        </div>
    );
};

CreateBlogPost.propTypes = {
    darkMode: PropTypes.bool.isRequired,
    onPostCreated: PropTypes.func,
    editPost: PropTypes.object
};

export default CreateBlogPost;
